/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 20/12/21
 */
import React, {memo, useCallback, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useGetOneEstablishment} from '@/modules/clients/hooks/useClientsEstablishment';
import ClientsEstablishmentApiService from '@/modules/clients/services/ClientsEstablishmentApiService';
import {useClientFilter} from '@/hooks/useClientFilter';
import {FilterFactory, InFilter, TermFilter} from '@dofleini/query-builder';
import {Form, Spin} from 'antd';
import {useGetClients} from '@/modules/auditorAudits/hooks/useGetClients';
import AsyncSelect from '@/components/AsyncSelect';

const clientId = (value) => {
  return (typeof value === 'object') ? value?._id : value;
};

const AuditorAuditsSelectEstablishment = ({value, filters, onChange, form, isViewMode, ...props}) => {
  const {data} = useGetOneEstablishment(clientId(value));
  const { currentClient, setCurrentClient, setCurrentClientEntity, setCurrentEstablishment, setCurrentEstablishmentEntity, currentEstablishmentEntity } = useClientFilter();

  const {data: clientData, isLoading} = useGetClients(new InFilter(
    {
      field: 'space',
      value: currentEstablishmentEntity?.secureSpacePath,
      objectId: true
    }).toQuery(), { enabled: !!currentEstablishmentEntity?.secureSpacePath });

  useEffect(() => {
    if(value) {
      setCurrentEstablishmentEntity(data);
      setCurrentEstablishment(clientId(value));
    } else {
      setCurrentEstablishmentEntity();
      setCurrentEstablishment();
    }
  }, [data, setCurrentEstablishment, setCurrentEstablishmentEntity, value]);

  useEffect(() => {
    if(value && clientData?.length > 0 && !currentClient?._id) {
      let client = clientData[clientData?.length - 1];
      setCurrentClient({_id: client?._id, space: client?.space});
      setCurrentClientEntity(client);
      form.setFieldsValue({client: client?._id});
    }
  }, [clientData, currentClient?._id, form, setCurrentClient, setCurrentClientEntity, value]);

  const onChangeHandler = useCallback( (value) => {
    if(currentClient?._id) {
      onChange(value);
    } else {
      onChange(value, currentClient);
    }
  }, [currentClient, onChange]);
  const newFilter = useMemo(() => {
    if (!currentClient?.space) {
      return filters;
    }
    return FilterFactory.add(filters, new TermFilter({field: 'secureSpacePath', value: currentClient?.space}))?.toQuery();
  }, [currentClient, filters]);

  if (isViewMode) {
    return (
      <>
        {data?.name || 'N/A'}
      </>
    );
  }

  return (
    <Spin spinning={isLoading}>
      <AsyncSelect
        queryKey={'establishment'}
        filters={newFilter}
        service={ClientsEstablishmentApiService}
        value={value}
        deferFetchUntilOpen={true}
        onChange={onChangeHandler}
        sort={{ code: 1 }}
        queryConfig={{ refetchOnWindowFocus: false }}
        className="custom-select"
        additionalParams={{
          forceSize: true,
          useProjections: true,
          projections: {
            name: 1,
            structure: 1,
            space: 1,
          }
        }}
        {...props}
      />
    </Spin>
  );
};

AuditorAuditsSelectEstablishment.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  filters: PropTypes.object,
  isViewMode: PropTypes.func,
  form: PropTypes.func,
};

export default memo(AuditorAuditsSelectEstablishment);

export const renderSelectEstablishment = ({formItemProps, form, field, ...props}) => {
  const onChange = (value = {}, currentClient) => {
    if (!currentClient?._id){
      form.setFieldsValue({[field.key]: value, service: null});
    } else {
      form.setFieldsValue({
        [field.key]: value,
        client: currentClient?._id,
        service: null,
      });
    }
  };

  const rules = [{
    required: field?.required,
    messaged: 'required',
  }];

  return (
    <Form.Item {...formItemProps} required={field?.required} rules={rules}>
      <AuditorAuditsSelectEstablishment onChange={onChange} form={form} {...props} placeholder={field?.placeholder} />
    </Form.Item>
  );
};

/**
 * cellRender
 * */
/**
 * This is not a component,
 * it is a function to render the Code component in a table cell
 * @param {String} text this is the value of the dataIndex of the table (we will ignored in this case)
 * @param {object} entity this is entity type
 * */
export const renderEstablishment = (text, entity) => {
  return (
    <AuditorAuditsSelectEstablishment value={text} entity={entity} isViewMode={true}/>
  );
};
